/**
 * Building blocks of the public pages (the home page and the guides), in the light and dark
 * themes: bands that run the full width of the screen, an aurora behind the openings (the brand's
 * colours on the light theme, soft light from above on the dark one), section introductions,
 * glass panels, and a type scale that grows with the screen.
 */

import type { ReactNode } from "react";

import { Reveal } from "./Reveal.js";
import { focusRing, styles } from "./ui.js";

/** Type set for the public pages. Sizes grow with the screen between their bounds. */
export const typeScale = {
  display:
    "font-display text-[clamp(2.75rem,1.55rem+4.4vw,5.25rem)] leading-[0.98] font-semibold tracking-[-0.045em] text-balance text-zinc-950 dark:text-white",
  title:
    "font-display text-[clamp(2.375rem,1.5rem+3.1vw,4rem)] leading-[1.02] font-semibold tracking-[-0.04em] text-balance text-zinc-950 dark:text-white",
  heading:
    "font-display text-[clamp(2rem,1.35rem+2.2vw,3.25rem)] leading-[1.05] font-semibold tracking-[-0.035em] text-balance text-zinc-950 dark:text-white",
  lead: "text-lg leading-8 text-zinc-600 sm:text-xl sm:leading-9 dark:text-zinc-400",
} as const;

/**
 * A panel of frosted glass: white on the light theme. On the dark one, a charcoal pane a step
 * lighter than the page, with a highlight along its top edge where the light catches it.
 */
export const glass =
  "rounded-3xl bg-white/75 shadow-[0_1px_2px_rgb(24_24_27/0.04),0_24px_48px_-28px_rgb(24_24_27/0.2)] ring-1 ring-zinc-900/[0.07] backdrop-blur-xl dark:bg-ink-900/75 dark:shadow-[inset_0_1px_0_rgb(255_255_255/0.06),0_24px_64px_-32px_rgb(0_0_0/0.7)] dark:ring-white/[0.08]";

/**
 * A tile inside a glass panel, such as a figure in an illustration: on the dark theme, a shade
 * lighter than the panel, as something laid on it rather than cut into it.
 */
export const inset =
  "rounded-xl bg-zinc-50/80 ring-1 ring-zinc-900/[0.06] ring-inset dark:bg-white/[0.035] dark:ring-white/[0.07]";

const tones = {
  /** The page's own ground. */
  base: "bg-white dark:bg-ink-950",
  /** A band set apart by a tinted ground and hairlines above and below. */
  raised: "border-y border-zinc-900/[0.06] bg-zinc-50 dark:border-white/[0.05] dark:bg-ink-900/50",
  /** The deepest ground, for code and proofs: on the dark theme, a shade below the page. */
  deep: "bg-zinc-100/70 dark:bg-black/25",
} as const;

/**
 * A section that spans the screen's width, with its content in the page's column. It clips what
 * overflows it, such as glows, without becoming a scroll container, so that content inside it
 * can stay in view as the page scrolls (`sticky`).
 */
export function Band({
  tone = "base",
  className = "",
  innerClassName = "",
  children,
  ...props
}: {
  tone?: keyof typeof tones;
  className?: string;
  innerClassName?: string;
  children: ReactNode;
  "aria-label"?: string;
  "aria-labelledby"?: string;
}) {
  return (
    <section {...props} className={`relative isolate overflow-clip ${tones[tone]} ${className}`}>
      <div className={`${styles.frame} ${innerClassName}`}>{children}</div>
    </section>
  );
}

/**
 * The aurora behind an opening or a closing panel. On the light theme: slow-drifting glows of the
 * brand's colours over a fading grid. On the dark theme: soft light falling from above, with only
 * a trace of the brand's blue and film grain that keeps the gradients from banding.
 * No grid, which reads as a pattern laid over the page there. It carries no content, so assistive
 * technology skips it.
 */
export function Aurora({
  fade = true,
}: {
  /** Whether it fades into the page's ground at the bottom, as a band's does. */
  fade?: boolean;
}) {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
      <div className="absolute -top-[22rem] left-[8%] h-[40rem] w-[46rem] rounded-full bg-brand-300/40 blur-[120px] motion-safe:animate-aurora dark:bg-brand-400/[0.045]" />
      <div className="absolute -top-[18rem] right-[2%] h-[36rem] w-[40rem] rounded-full bg-sky-300/30 blur-[120px] [animation-delay:-6s] motion-safe:animate-aurora dark:bg-brand-400/[0.03]" />
      <div className="absolute top-[30%] left-[38%] h-[26rem] w-[34rem] rounded-full bg-brand-200/45 blur-[110px] [animation-delay:-12s] motion-safe:animate-aurora dark:hidden" />
      <div className="bg-spotlight absolute inset-x-0 top-0 hidden h-[44rem] dark:block" />
      <div className="bg-grid absolute inset-0 dark:hidden" />
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-zinc-900/10 to-transparent dark:via-white/10" />
      <div className="bg-noise absolute inset-0 hidden opacity-[0.03] mix-blend-overlay dark:block" />
      {fade && (
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-white dark:to-ink-950" />
      )}
    </div>
  );
}

/** A small label above a heading, set in a glass pill with the brand's dot. */
export function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <p className="inline-flex items-center gap-2 rounded-full bg-white/70 py-1 pr-3 pl-2.5 text-sm font-medium text-zinc-700 shadow-sm ring-1 ring-zinc-900/10 ring-inset backdrop-blur dark:bg-white/[0.04] dark:text-zinc-200 dark:shadow-none dark:ring-white/10">
      <span aria-hidden className="relative flex size-2">
        <span className="absolute inset-0 rounded-full bg-brand-400 motion-safe:animate-ping motion-safe:[animation-duration:2.4s]" />
        <span className="relative size-2 rounded-full bg-gradient-to-br from-brand-400 to-brand-600 dark:from-brand-300 dark:to-brand-500" />
      </span>
      {children}
    </p>
  );
}

/** A section's eyebrow, heading and introduction, rising into place as it scrolls into view. */
export function SectionIntro({
  id,
  eyebrow,
  title,
  children,
  center = false,
}: {
  id?: string;
  eyebrow?: string;
  title: string;
  children?: ReactNode;
  center?: boolean;
}) {
  return (
    <Reveal className={center ? "mx-auto max-w-3xl text-center" : "max-w-3xl"}>
      {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
      <h2 id={id} className={`${eyebrow ? "mt-6" : ""} ${typeScale.heading}`}>
        {title}
      </h2>
      {children && (
        <div className={`mt-5 ${typeScale.lead} ${center ? "mx-auto max-w-2xl" : "max-w-2xl"}`}>
          {children}
        </div>
      )}
    </Reveal>
  );
}

/** The closing panel of a public page: a call to action over the aurora. */
export function CallToActionBand({
  title,
  children,
  actions,
}: {
  title: string;
  children?: ReactNode;
  actions: ReactNode;
}) {
  return (
    <Band innerClassName="py-20 sm:py-28">
      <Reveal>
        <div className="ring-gradient relative isolate overflow-hidden rounded-[2rem] bg-gradient-to-br from-brand-50 via-white to-brand-100 px-6 py-16 text-center sm:px-12 sm:py-24 dark:from-ink-900 dark:via-ink-900 dark:to-ink-950">
          <Aurora fade={false} />
          <h2 className={`mx-auto max-w-3xl ${typeScale.heading}`}>{title}</h2>
          {children && (
            <p className="mx-auto mt-5 max-w-xl text-lg leading-8 text-zinc-600 dark:text-zinc-300">
              {children}
            </p>
          )}
          <div className="mt-10 flex flex-wrap justify-center gap-3">{actions}</div>
        </div>
      </Reveal>
    </Band>
  );
}

/** The public pages' buttons: the brand's blue, and glass. */
export const bandButtons = {
  primary: styles.primaryButtonLarge,
  secondary: `inline-flex items-center justify-center gap-2 rounded-lg bg-white/80 px-5 py-3 text-base font-semibold whitespace-nowrap text-zinc-900 shadow-sm ring-1 ring-zinc-900/10 backdrop-blur transition ring-inset hover:bg-white hover:ring-zinc-900/20 dark:bg-white/[0.06] dark:text-white dark:shadow-none dark:ring-white/15 dark:hover:bg-white/[0.1] dark:hover:ring-white/25 ${focusRing}`,
} as const;
